import { appError, err, ok, } from '@orbit-mind/shared-types';
const BOUNDARY_CODES = {
    network: 'VALIDATION_FAILED',
    storage: 'MAP_CORRUPTED',
    import: 'VALIDATION_FAILED',
    ai: 'AI_OUTPUT_INVALID',
    url: 'VALIDATION_FAILED',
    internal: 'INTERNAL',
};
function flatten(error) {
    return error.issues.map((issue) => ({
        path: issue.path.map(String).join('.'),
        message: issue.message,
    }));
}
/**
 * Caps how many issues travel with a failure.
 *
 * A file with a systematic problem produces one issue per node. Ten is enough for a person to
 * see the pattern; ten thousand is a log line nobody reads and a payload nobody wants.
 */
export const MAX_REPORTED_ISSUES = 10;
/**
 * Parses `value` against `schema`, reporting where it came from.
 *
 * `context` names the thing being parsed — "map document", "node", "AI map result" — and appears
 * in the message. It is developer-facing: the user sees a translated string chosen from the
 * error code, never this.
 */
export function parseAt(schema, value, boundary, context) {
    const parsed = schema.safeParse(value);
    if (parsed.success)
        return ok(parsed.data);
    const issues = flatten(parsed.error);
    const shown = issues.slice(0, MAX_REPORTED_ISSUES);
    const base = appError(BOUNDARY_CODES[boundary], `invalid ${context} from ${boundary}`, {
        details: {
            boundary,
            context,
            issueCount: issues.length,
            // The path of the first failure is the single most useful thing in a bug report, and it
            // carries no document content.
            firstPath: shown[0]?.path ?? '',
        },
        // A malformed value does not become well-formed on a second attempt.
        retryable: false,
    });
    return err({ ...base, issues: shown });
}
/** Parses a value already known to be well-formed. Throws, because a failure here is a bug. */
export function parseInternal(schema, value, context) {
    const parsed = schema.safeParse(value);
    if (parsed.success)
        return parsed.data;
    const issues = flatten(parsed.error).slice(0, MAX_REPORTED_ISSUES);
    throw new Error(`internal invariant violated while parsing ${context}: ${issues
        .map((issue) => `${issue.path || '<root>'}: ${issue.message}`)
        .join('; ')}`);
}
/**
 * Parses JSON text and then validates it.
 *
 * Kept together because the two failures are indistinguishable to a caller — both mean "this
 * file is not what it claims to be" — and splitting them makes every call site write the same
 * two-step dance.
 */
export function parseJsonAt(schema, text, boundary, context) {
    let value;
    try {
        value = JSON.parse(text);
    }
    catch (cause) {
        const base = appError(BOUNDARY_CODES[boundary], `malformed JSON for ${context}`, {
            details: { boundary, context, issueCount: 1, firstPath: '' },
            retryable: false,
            cause,
        });
        return err({
            ...base,
            issues: [{ path: '', message: 'not valid JSON' }],
        });
    }
    return parseAt(schema, value, boundary, context);
}
/**
 * Validates each element, keeping the good ones and reporting the bad.
 *
 * The one place partial results are right: replaying a journal of operations, or importing a
 * file with one damaged node. Discarding a whole session's work because entry 400 is corrupt
 * loses more than it protects. The caller gets both halves and decides.
 */
export function parseEach(schema, values, boundary, context) {
    const valid = [];
    const failures = [];
    values.forEach((value, index) => {
        const result = parseAt(schema, value, boundary, `${context}[${String(index)}]`);
        if (result.ok)
            valid.push(result.value);
        else
            failures.push({ index, failure: result.error });
    });
    return { valid, failures };
}
/** Is a value valid, without wanting the parsed result or the reason? */
export function isValid(schema, value) {
    return schema.safeParse(value).success;
}
//# sourceMappingURL=boundary.js.map